/* global __webpack_init_sharing__, __webpack_share_scopes__ */

/**
 * Tracks which remote containers have already been loaded
 * to prevent injecting the same remoteEntry.js script multiple times.
 */
const loadedRemotes = new Set();
/**
 * Tracks per-container initialization so `container.init()` runs
 * at most once per remote container.
 *
 * Keyed by the container object itself (not the remote name), and
 * stores the in-flight promise so concurrent callers await the same
 * initialization instead of racing into a double `init()` call.
 *
 * Using a WeakMap keyed by the container means that if a remote is ever
 * reloaded and `window[remoteName]` points to a fresh container instance,
 * the new object gets its own initialization rather than colliding with a
 * stale entry. Entries are also garbage-collected automatically.
 */
const containerInitPromises = new WeakMap();

/**
 * Hostnames a remoteEntry.js is allowed to be served from, supplied per
 * environment as a comma-separated list (e.g. "teladochealth.com,localhost").
 */
const ALLOWED_REMOTE_HOSTNAMES = (
    window?.process?.env?.ALLOWED_REMOTE_HOSTNAMES ??
    process.env.ALLOWED_REMOTE_HOSTNAMES ??
    ''
)
    .split(',')
    .map(hostname => hostname.trim().toLowerCase())
    .filter(Boolean);

const isAllowedRemoteUrl = url => {
    try {
        // eslint-disable-next-line node/prefer-global/url
        const {protocol, hostname} = new URL(url);
        const isHttps = protocol === 'https:';
        const isLocalhost = hostname === 'localhost';
        const isTrustedHost = ALLOWED_REMOTE_HOSTNAMES.some(
            allowed => hostname === allowed || hostname.endsWith(`.${allowed}`)
        );

        return (isHttps || isLocalhost) && isTrustedHost;
    } catch {
        return false;
    }
};

/**
 * Mapping between remote container names and their remoteEntry URLs.
 *
 * The URL can be configured through environment variables to support
 * different environments (local, staging, production).
 *
 * Example format: http://localhost:8100/remoteEntry.js
 */
const REMOTE_ENTRY_MAP = {
    feDspApp:
        window?.process?.env?.DSP_MICROAPP_URL ?? process.env.DSP_MICROAPP_URL, // Drug Search & Pricing Microapp
};

const IS_PROD = process.env.ENVIRONMENT === 'production';

/**
 *
 * @param {string} remoteName
 * @desc
 * Dynamically injects a remote micro-frontend's `remoteEntry.js`
 * into the document only once.
 *
 * This is required for Webpack Module Federation to load remote
 * containers at runtime.
 *
 * The loader ensures:
 *
 * - The script is injected only once per remote
 * - Multiple concurrent requests reuse the same script
 * - Errors are handled properly
 *
 * Loaded remotes are cached in `loadedRemotes` to prevent duplicate
 * script injections.
 *
 * @returns {Promise<void>}
 *
 * Used internally by `loadRemoteModule`.
 */
const injectRemoteEntry = remoteName => {
    return new Promise((resolve, reject) => {
        /**
         * If remote was already loaded earlier, skip injection.
         */
        if (loadedRemotes.has(remoteName)) {
            resolve();

            return;
        }

        /**
         * If the container already exists on window,
         * mark it as loaded and continue.
         */
        if (window[remoteName]) {
            loadedRemotes.add(remoteName);
            resolve();

            return;
        }

        /**
         * Check if the script tag already exists in the DOM.
         * This prevents duplicate injection when multiple components
         * request the same remote simultaneously.
         */
        const existing = document.querySelector(
            `script[data-remote="${remoteName}"]`
        );

        if (existing) {
            existing.addEventListener('load', () => {
                loadedRemotes.add(remoteName);
                resolve();
            });

            existing.addEventListener('error', () => {
                existing.remove();
                reject(
                    new Error(
                        `remoteEntry.js for "${remoteName}" was injected but failed to load.`
                    )
                );
            });

            return;
        }

        /**
         * Resolve remoteEntry URL for this remote container.
         */
        const url = REMOTE_ENTRY_MAP[remoteName];

        if (!url) {
            reject(
                new Error(`No remoteEntry URL configured for "${remoteName}".`)
            );

            return;
        }

        if (!isAllowedRemoteUrl(url)) {
            reject(
                new Error(
                    `Blocked untrusted remoteEntry URL for "${remoteName}": ${url}`
                )
            );

            return;
        }

        if (!IS_PROD) {
            // eslint-disable-next-line no-console
            console.log(`[MF] Injecting remote "${remoteName}" from ${url}`);
        }

        /**
         * Create and inject script tag for the remote entry.
         */
        const script = document.createElement('script');

        script.src = url;
        script.async = true;
        script.dataset.remote = remoteName;

        script.onload = () => {
            if (!IS_PROD) {
                // eslint-disable-next-line no-console
                console.log(`[MF] Loaded remote "${remoteName}"`);
            }
            loadedRemotes.add(remoteName);
            resolve();
        };

        script.onerror = () => {
            script.remove();
            reject(
                new Error(
                    `Failed to load remoteEntry.js for "${remoteName}" from ${url}.`
                )
            );
        };

        document.head.appendChild(script);
    });
};

/**
 *
 * @param {object} container
 * @desc
 * Initializes the Webpack shared module scope and the remote container
 * exactly once per container.
 *
 * In Module Federation, calling `container.init()` more than once on the
 * same container can throw (e.g. "container already initialized"), which
 * would break every subsequent load. This guard ensures the real work runs
 * only on the first call; later calls reuse the cached promise, and
 * concurrent callers await the same initialization instead of racing into a
 * double `init()`.
 *
 * On failure the cache entry is cleared so a later call can retry rather than
 * being permanently poisoned by a transient error.
 *
 * @returns {Promise<void>}
 *
 * Used internally by `loadRemoteModule`.
 */
const initContainer = container => {
    if (!containerInitPromises.has(container)) {
        const promise = (async () => {
            /**
             * Initialize shared module scope so host and remote
             * can share dependencies (React, libraries, etc.).
             */
            await __webpack_init_sharing__('default');

            // eslint-disable-next-line camelcase
            await container.init(__webpack_share_scopes__.default);
        })().catch(error => {
            // Allow a future call to retry rather than caching the failure.
            containerInitPromises.delete(container);
            throw error;
        });

        containerInitPromises.set(container, promise);
    }

    return containerInitPromises.get(container);
};

/**
 *
 * @param {string} remoteName
 * @param {string} exposedModule
 *
 * @desc
 * Dynamically loads a module exposed by a remote micro-frontend
 * using Webpack Module Federation.
 *
 * Workflow:
 *
 * 1. Ensure the remote container's `remoteEntry.js` is injected
 * 2. Initialize Webpack's shared module scope
 * 3. Initialize the remote container with the shared scope
 * 4. Request the exposed module from the container
 * 5. Return the loaded module
 *
 * Example:
 *
 * loadRemoteModule('feDspApp', 'PrescriptionDetailsWidgetSequence')
 *
 * This resolves:
 *
 * window.feDspApp.get('./PrescriptionDetailsWidgetSequence')
 *
 * @returns {Promise<any>} resolved module factory result
 *
 * import { loadRemoteModule } from '@/utils/remote-module-loader';
 */
export const prefetchRemoteEntry = remoteName => {
    injectRemoteEntry(remoteName).catch(() => {
        // Prefetch failure is silent — loadRemoteModule will retry when the route loads.
    });
};

export const loadRemoteModule = async (remoteName, exposedModule) => {
    /**
     * Ensure remoteEntry.js is loaded.
     */
    await injectRemoteEntry(remoteName);

    const container = window[remoteName];

    if (!container) {
        throw new Error(
            `Container "${remoteName}" not found on window. Ensure ModuleFederationPlugin name matches.`
        );
    }

    /**
     * Initialize shared scope + container once per container.
     * Safe to call repeatedly; only the first call does real work.
     */
    await initContainer(container);

    /**
     * Retrieve exposed module from the remote container.
     */
    const factory = await container.get(`./${exposedModule}`);

    return factory();
};
